import { AxiosError } from 'axios';
import { MouseEventHandler, useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import styled from 'styled-components';

import { getRefundList } from '@/api';
import FlexWrap from '@/components/common/FlexWrap';
import LoadingView from '@/components/common/LoadingView';
import UploadItem from '@/components/UploadImage/UploadItem';
import { useAppSelector } from '@/hooks/useReduxHooks';
import { IGlobalState } from '@/store';
import {
  IGetRefundListPayload,
  IGetRefundListResponse,
} from '@/types/common.type';
import { IMG_RECEIPT_LEAFLET } from '@repo/assets';

const TITLE = {
  KOR: {
    title: '도장 받은 영수증 업로드',
    subTitle: '세관에서 도장을 받은 환급 영수증을 사진으로 업로드해 주세요.',
    cruiseSubTitle:
      '세관에 제출한 환급 영수증을 확인한 후 환급액을 송금해드립니다.',
  },
  ENG: {
    title: 'Upload Refund Receipt',
    subTitle: 'Please upload the receipt with customs stamp as shown below.',
    cruiseSubTitle:
      'Refund will be completed after we verify your Customs-submitted receipt.',
  },
};

type Props = {
  refetchHome: () => void;
};

function UploadReceiptImage({ refetchHome }: Props) {
  const { passportNumber, nationality } = useAppSelector(
    (state) => state,
  ) as IGlobalState;
  const isCruise =
    passportNumber && localStorage.getItem(passportNumber) === 'Cruise';
  const LANG = nationality === 'KOR' ? 'KOR' : 'ENG';
  const [isLatest, setIsLatest] = useState(true);
  const [data, setData] = useState<null | IGetRefundListResponse>(null);

  const onClickFilter: MouseEventHandler<HTMLButtonElement> = (e: any) => {
    setIsLatest(e.target.name === 'LATEST');
  };

  const getRefundListMutation = useMutation<
    IGetRefundListResponse,
    AxiosError,
    IGetRefundListPayload
  >((payload) => getRefundList(payload), {
    retry: false,
    onSuccess: (list) => {
      setData(list);
    },
    onError: () => {
      alert(`[error code:0000 ]서버요청에 실패하였습니다. 다시 시도해주세요.`);
    },
  });

  useEffect(() => {
    getRefundListMutation.mutate({
      passportNumber,
      refundAfter: true,
      latest: isLatest,
    });
  }, [isLatest]);

  return (
    <>
      <Title>{TITLE[LANG].title}</Title>
      <Desc>
        {isCruise ? TITLE[LANG].cruiseSubTitle : TITLE[LANG].subTitle}
      </Desc>
      <Image src={IMG_RECEIPT_LEAFLET} />
      {getRefundListMutation.isLoading ? (
        <LoadingView />
      ) : (
        <>
          {data && data.length > 0 ? (
            data?.map((item, idx) => (
              <UploadItem {...item} key={idx} refetchHome={refetchHome} />
            ))
          ) : (
            <NoContent>There is no purchase history.</NoContent>
          )}
        </>
      )}
    </>
  );
}
const Title = styled.h3`
  font-size: 18px;
  line-height: 150%;
  margin-top: 8px;
  padding: 20px 0 0;
  border-top: 1px solid #cbccce;
`;
const Desc = styled.p`
  font-size: 14px;
  line-height: 150%;
  font-weight: 400;
`;
const Image = styled.img`
  width: 100%;
  padding: 20px 0;
  border-bottom: 1px solid #cbccce;
`;
const FilterBox = styled(FlexWrap)`
  width: 100%;
  margin-top: 20px;
`;
const FilterBtn = styled.button<{ active: boolean }>`
  color: ${(props) => (props.active ? '#000000' : '#9FA1A7')};
  font-size: 14px;
  :last-child {
    ::before {
      content: ' | ';
      margin: 0 8px;
      color: '#9FA1A7';
    }
  }
`;
const NoContent = styled.p`
  font-size: 18px;
  line-height: 26px;
  color: #9fa1a7;
  margin: 222px 0;
  @media ${(props) => props.theme.mobile} {
    font-size: 14px;
    line-height: 22px;
    text-align: left;
    margin: 222px 8px;
  }
`;
export default UploadReceiptImage;
